const API = process.env.REACT_APP_BACKEND_URL;

export const imgUrl = (p) => (!p ? p : p.startsWith('http') ? p : `${API}${p}`);

const mapImages = (prod) => ({
  ...prod,
  image: imgUrl(prod.image),
  video: imgUrl(prod.video),
  videos: prod.videos ? prod.videos.map(imgUrl) : undefined,
  gallery: (prod.gallery || []).map(imgUrl),
  lookbook: (prod.lookbook || []).map(imgUrl),
  color_images: prod.color_images
    ? Object.fromEntries(Object.entries(prod.color_images).map(([k, v]) => [k, imgUrl(v)]))
    : undefined,
  color_galleries: prod.color_galleries
    ? Object.fromEntries(Object.entries(prod.color_galleries).map(([k, arr]) => [k, (arr || []).map(imgUrl)]))
    : undefined,
});

export const fetchProducts = async (category) => {
  const url = category ? `${API}/api/products?category=${category}` : `${API}/api/products`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to load products');
  const data = await res.json();
  return data.map(mapImages);
};

export const fetchProduct = async (id) => {
  const res = await fetch(`${API}/api/products/${id}`);
  if (!res.ok) return null;
  return mapImages(await res.json());
};

export const notifyMe = async (productId, email) => {
  const res = await fetch(`${API}/api/notify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ product_id: productId, email }),
  });
  if (!res.ok) throw new Error(await parseErr(res, 'Could not sign up. Please try again.'));
  return res.json();
};

export const saveCart = async (email, items) => {
  const res = await fetch(`${API}/api/cart/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      origin_url: window.location.origin,
      items: items.map((it) => ({
        id: it.id, name: it.name, image: it.image,
        price: it.price, quantity: it.quantity,
        length: it.length, color: it.color,
      })),
    }),
  });
  if (!res.ok) throw new Error(await parseErr(res, 'Could not save your bag. Please try again.'));
  return res.json();
};


export const fetchNotifySignups = async () => {
  const res = await fetch(`${API}/api/admin/notify-signups`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load signups');
  return res.json();
};

export const createCheckout = async (items, originUrl) => {
  const res = await fetch(`${API}/api/payments/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, origin_url: originUrl }),
  });
  if (!res.ok) throw new Error('Failed to start checkout');
  return res.json();
};

export const getPaymentStatus = async (sessionId) => {
  const res = await fetch(`${API}/api/payments/status/${sessionId}`);
  if (!res.ok) throw new Error('Failed to get status');
  return res.json();
};

export const getOrder = async (orderId) => {
  const res = await fetch(`${API}/api/orders/${orderId}`);
  if (!res.ok) return null;
  return res.json();
};

// -------------------------------------------------- Admin
const ADMIN_TOKEN_KEY = 'nnl_admin_token';
export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const setAdminToken = (t) => localStorage.setItem(ADMIN_TOKEN_KEY, t);
export const clearAdminToken = () => localStorage.removeItem(ADMIN_TOKEN_KEY);
const authHeaders = () => ({ Authorization: `Bearer ${getAdminToken()}` });

const parseErr = async (res, fallback) => {
  const e = await res.json().catch(() => ({}));
  const d = e.detail;
  return typeof d === 'string' ? d : fallback;
};

export const adminLogin = async (email, password) => {
  const res = await fetch(`${API}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error(await parseErr(res, 'Login failed'));
  return res.json();
};

export const adminMe = async () => {
  const res = await fetch(`${API}/api/auth/me`, { headers: authHeaders() });
  if (!res.ok) throw new Error('unauthenticated');
  return res.json();
};

export const fetchAdminOrders = async () => {
  const res = await fetch(`${API}/api/admin/orders`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load orders');
  return res.json();
};

export const fetchAdminStats = async () => {
  const res = await fetch(`${API}/api/admin/stats`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load stats');
  return res.json();
};

export const fetchAdminAnalytics = async (days) => {
  const q = days ? `?days=${days}` : '';
  const res = await fetch(`${API}/api/admin/analytics${q}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load analytics');
  return res.json();
};

export const updateOrderStatus = async (orderId, status) => {
  const res = await fetch(`${API}/api/admin/orders/${orderId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error(await parseErr(res, 'Update failed'));
  return res.json();
};

export const refundOrder = async (orderId) => {
  const res = await fetch(`${API}/api/admin/orders/${orderId}/refund`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await parseErr(res, 'Refund failed'));
  return res.json();
};

export const getAnnouncements = async () => {
  const res = await fetch(`${API}/api/announcements`);
  if (!res.ok) throw new Error('Failed to load announcements');
  return res.json();
};

export const adminGetAnnouncements = async () => {
  const res = await fetch(`${API}/api/admin/announcements`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load announcements');
  return res.json();
};

export const adminSaveAnnouncements = async (items) => {
  const res = await fetch(`${API}/api/admin/announcements`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) throw new Error(await parseErr(res, 'Save failed'));
  return res.json();
};

export const getActiveSales = async () => {
  const res = await fetch(`${API}/api/sales/active`);
  if (!res.ok) throw new Error('Failed to load sales');
  return res.json();
};

export const adminGetBannerStyle = async () => {
  const res = await fetch(`${API}/api/admin/sale-banner`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load banner style');
  return res.json();
};

export const adminSaveBannerStyle = async (style) => {
  const res = await fetch(`${API}/api/admin/sale-banner`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(style),
  });
  if (!res.ok) throw new Error(await parseErr(res, 'Save failed'));
  return res.json();
};

// -------------------------------------------------- Sale Scheduler
export const adminGetSales = async () => {
  const res = await fetch(`${API}/api/admin/sales`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to load sales');
  return res.json();
};

export const adminSaveSales = async (items) => {
  const res = await fetch(`${API}/api/admin/sales`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) throw new Error(await parseErr(res, 'Save failed'));
  return res.json();
};

// -------------------------------------------------- Promo Code Manager
export const adminGetPromoCodes = async () => {
  const res = await fetch(`${API}/api/admin/promo-codes`, { headers: authHeaders() });
  if (!res.ok) throw new Error(await parseErr(res, 'Failed to load promo codes'));
  return res.json();
};

export const adminCreatePromoCode = async (code, percent, expires) => {
  const res = await fetch(`${API}/api/admin/promo-codes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ code, percent, expires: expires || null }),
  });
  if (!res.ok) throw new Error(await parseErr(res, 'Could not create code'));
  return res.json();
};

export const adminDeactivatePromoCode = async (promoId) => {
  const res = await fetch(`${API}/api/admin/promo-codes/${promoId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await parseErr(res, 'Could not deactivate code'));
  return res.json();
};
